"use client";

import { useState } from "react";
import Link from "next/link";
import { Bell, CheckCircle2, Users, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/auth-context";
import { indefiniteArticle } from "@/lib/utils";

interface CertificationInterestFormProps {
  certSlug: string;
  certName: string;
  initialCount?: number;
  variant?: "default" | "compact";
  showStartLink?: boolean;
}

export function CertificationInterestForm({
  certSlug,
  certName,
  initialCount = 0,
  variant = "default",
  showStartLink = true,
}: CertificationInterestFormProps) {
  const { user, isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">(
    "idle"
  );
  const [error, setError] = useState("");
  const [count, setCount] = useState(initialCount);
  const [thresholdReached, setThresholdReached] = useState(false);

  const startGroupHref = `/study-groups/create?certification=${encodeURIComponent(
    certName
  )}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const emailToSend = isAuthenticated ? user?.email ?? "" : email.trim();
    if (!isAuthenticated && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToSend)) {
      setError("Please enter a valid email address.");
      return;
    }

    setStatus("submitting");
    try {
      const res = await fetch("/api/certification-interest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ certSlug, certName, email: emailToSend }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Something went wrong.");
      }
      setCount(typeof data.count === "number" ? data.count : count + 1);
      setThresholdReached(!!data.thresholdReached);
      setStatus("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div
        className={
          variant === "compact"
            ? "text-left"
            : "rounded-xl border border-green-200 bg-green-50 p-5 text-center"
        }
      >
        {variant !== "compact" && (
          <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
        )}
        <p className="font-semibold text-green-800">You&rsquo;re on the list!</p>
        <p className="text-sm text-green-700 mt-1">
          We&rsquo;ll email you the moment {indefiniteArticle(certName)} {certName} study group forms.
          {count > 0 && (
            <>
              {" "}
              <span className="font-medium">
                {count} {count === 1 ? "person is" : "people are"} waiting.
              </span>
            </>
          )}
        </p>
        {thresholdReached && (
          <p className="text-sm text-green-800 mt-2 font-medium">
            Enough people are waiting to form a group — want to start it?
          </p>
        )}
        {showStartLink && (
          <Button asChild className="mt-4" size="sm">
            <Link href={startGroupHref}>
              Start the {certName} study group
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Link>
          </Button>
        )}
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div className="flex flex-col flex-1">
        <p className="text-xs font-medium text-gray-800">
          Not ready to host?
        </p>
        <p className="text-[11px] text-gray-500 mt-0.5 mb-2 leading-relaxed flex-1">
          {count > 0 ? (
            <>
              {count} {count === 1 ? "person is" : "people are"} already waiting
              for {certName}. We&rsquo;ll email you when a group forms.
            </>
          ) : (
            <>We&rsquo;ll email you when someone else starts {indefiniteArticle(certName)} {certName} group.</>
          )}
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          {!isAuthenticated && (
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="bg-white h-11 text-base"
              aria-label="Email address"
              required
            />
          )}
          <Button
            type="submit"
            disabled={status === "submitting"}
            variant="outline"
            className="w-full h-11 text-sm"
          >
            {status === "submitting" ? "Adding you…" : "Notify me"}
          </Button>
        </form>
        <p className="text-[11px] text-gray-500 mt-1.5">
          We&rsquo;ll email you when a study group is created for {certName}.
        </p>
        {isAuthenticated && (
          <p className="text-[11px] text-gray-400 mt-1.5">
            We&rsquo;ll use {user?.email}.
          </p>
        )}
        {error && <p className="text-xs text-red-600 mt-1.5">{error}</p>}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-5">
      <div className="flex items-start gap-3 mb-3">
        <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
          <Bell className="h-4 w-4 text-white" />
        </div>
        <div>
          <p className="font-semibold text-gray-900">
            Get notified when {indefiniteArticle(certName)} {certName} study group forms
          </p>
          <p className="text-sm text-gray-600 mt-0.5">
            {count > 0 ? (
              <span className="inline-flex items-center gap-1">
                <Users className="h-3.5 w-3.5 text-blue-600" />
                {count} {count === 1 ? "person is" : "people are"} already waiting
                for {certName}.
              </span>
            ) : (
              <>Be the first to raise your hand for {certName}.</>
            )}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
        {!isAuthenticated && (
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="bg-white"
            aria-label="Email address"
            required
          />
        )}
        <Button type="submit" disabled={status === "submitting"} className="shrink-0">
          {status === "submitting" ? "Adding you…" : "Notify me"}
        </Button>
      </form>
      <p className="text-xs text-gray-500 mt-2">
        We&rsquo;ll email you when a study group is created for {certName}.
      </p>

      {isAuthenticated && (
        <p className="text-xs text-gray-500 mt-2">
          We&rsquo;ll use your account email ({user?.email}).
        </p>
      )}
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}

      {showStartLink && (
        <div className="mt-3 pt-3 border-t border-blue-100 text-center">
          <Link
            href={startGroupHref}
            className="text-sm font-medium text-blue-700 hover:text-blue-800 inline-flex items-center gap-1"
          >
            Or start the group yourself
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}
